import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiJson } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Megaphone } from 'lucide-react';
// Phase 1 Repoint: Using SnapshotEditWorkspace instead of legacy DetailedCampaignEditModal
import { SnapshotEditWorkspace } from '@/components/campaigns/SnapshotEditWorkspace';
import { CloneCampaignModal } from '@/components/admin/CloneCampaignModal';
import { useLocation } from 'wouter';
import { useCampaignFilters } from '@/context/CampaignFiltersContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { dbg } from '@/lib/dbg';
import { getFlag } from '@/utils/featureFlag';
import { Input } from '@/components/ui/input';
import { useUpdateCampaignStatus, Campaign, CampaignsPaginatedResponse } from '@/api/campaigns';
import { Toaster } from '@/components/ui/sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState } from '@/components/ui/error-state';
import { TableSkeleton } from '@/components/skeletons/table-skeleton';
import { DataTable } from '@/components/tables/data-table';
import { createCampaignColumns } from '@/components/tables/campaign-columns';
import { CampaignBulkActionsBar } from '@/components/campaigns/CampaignBulkActionsBar';
import { YearFilter } from '@/components/filters/YearFilter';
import { useYear, useHydrateYearFromStorage } from '@/state/yearStore';
import { useUrlYear } from '@/hooks/useUrlYear';
import {
  buildCampaignDetailUrl,
  parseCampaignsListUrl,
  saveCampaignsListReturnState,
  syncCampaignsListUrlToBrowser,
  type CampaignsListUrlState,
} from '@/lib/campaignsListUrl';
import { compareCyclesNewestFirst } from '@/lib/bookingCycles';

export default function CampaignsPage() {
  const [, navigate] = useLocation();
  const { retailerId, cycleId, setRetailerId, setCycleId } = useCampaignFilters();
  const initialListUrlState = useMemo(() => parseCampaignsListUrl(window.location.search), []);
  const urlCampaignFilters = useMemo(() => ({
    retailerId: initialListUrlState.retailerId,
    cycleId: initialListUrlState.cycleId,
  }), [initialListUrlState.retailerId, initialListUrlState.cycleId]);

  // Global year filter - hydrate from localStorage first to avoid flickering
  useHydrateYearFromStorage();
  const year = useYear();
  useUrlYear();

  const [includeArchived, setIncludeArchived] = useState(initialListUrlState.includeArchived);
  const [page, setPage] = useState(initialListUrlState.page);
  const [search, setSearch] = useState(initialListUrlState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialListUrlState.search);
  const pageSize = 20;
  const showIncludeArchived = getFlag('UI_INCLUDE_ARCHIVED', true, true);
  const [updatingCampaignId, setUpdatingCampaignId] = useState<number | null>(null);
  const [detailedEditModalOpen, setDetailedEditModalOpen] = useState<number | null>(null);
  const [cloneModalState, setCloneModalState] = useState<{ id: number; name: string; retailerId: number | null } | null>(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const prevSearchRef = React.useRef<string | null>(null);

  // Debounce search input to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (prevSearchRef.current !== null && prevSearchRef.current !== search) {
        setPage(1);
      }
      prevSearchRef.current = search;
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Mutation hook for updating campaign status
  const updateCampaignStatusMutation = useUpdateCampaignStatus();

  const handleYearChange = useCallback((_nextYear: number) => {
    setPage(1);
    if (!urlCampaignFilters.cycleId) {
      setCycleId(null);
    }
  }, [setCycleId, urlCampaignFilters.cycleId]);

  useEffect(() => {
    if (urlCampaignFilters.retailerId) {
      setRetailerId(urlCampaignFilters.retailerId);
    }
    if (urlCampaignFilters.cycleId) {
      setCycleId(urlCampaignFilters.cycleId);
    }
  }, [setRetailerId, setCycleId, urlCampaignFilters.retailerId, urlCampaignFilters.cycleId]);

  const campaignsListState = useMemo((): CampaignsListUrlState => ({
    retailerId,
    cycleId,
    year,
    page,
    search: debouncedSearch,
    includeArchived,
  }), [retailerId, cycleId, year, page, debouncedSearch, includeArchived]);

  useEffect(() => {
    syncCampaignsListUrlToBrowser(campaignsListState);
  }, [campaignsListState]);

  // React Query with server-side pagination and search
  const { data: paginatedResponse, isLoading, error } = useQuery<CampaignsPaginatedResponse, Error>({
    queryKey: ['/api/campaigns', retailerId, cycleId, includeArchived, page, pageSize, debouncedSearch, year],
    queryFn: async () => {
      dbg.log('[QUERY_DEBUG] Fetching /api/campaigns with filters', { retailerId, cycleId, includeArchived, page, pageSize, search: debouncedSearch, year });
      const params = new URLSearchParams();
      if (typeof retailerId === 'number') params.set('retailerId', String(retailerId));
      if (typeof cycleId === 'number') params.set('cycleId', String(cycleId));
      params.set('includeArchived', includeArchived ? 'true' : 'false');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      params.set('year', String(year));
      if (debouncedSearch) params.set('search', debouncedSearch);
      const qs = params.toString();
      const rawData = await apiJson<CampaignsPaginatedResponse | Campaign[]>(`/api/campaigns?${qs}`);

      // Handle both paginated response and legacy array response
      if (Array.isArray(rawData)) {
        return {
          data: rawData,
          totalCount: rawData.length,
          page: 1,
          pageSize: rawData.length,
        };
      }

      return rawData;
    },
    staleTime: 0, // No caching - always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true, // Refetch when window regains focus to ensure fresh data
    placeholderData: (previousData) => previousData,
  });

  const campaigns = paginatedResponse?.data || [];
  const totalCount = paginatedResponse?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const hasActiveListFilters = Boolean(
    search.trim() ||
    debouncedSearch.trim() ||
    retailerId !== null ||
    cycleId !== null ||
    includeArchived
  );
  const showCreateCampaignEmptyState = campaigns.length === 0 && totalCount === 0 && !hasActiveListFilters;

  // Retailers list for filter
  const { data: retailers = [] } = useQuery<any[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => apiJson('/api/retailers'),
    staleTime: 60_000,
  });

  // Cycles list depends on retailerId
  const enableCycles = typeof retailerId === 'number';
  const { data: rawCycles = [], isError: cyclesError } = useQuery<any[]>({
    queryKey: ['/api/retailers', retailerId, 'cycles'],
    queryFn: async () => apiJson(`/api/retailers/${retailerId}/cycles`),
    enabled: enableCycles,
    staleTime: 60_000,
  });

  // Filter cycles by selected year so the dropdown matches the year pill
  const cycles = useMemo(() => {
    if (!Array.isArray(rawCycles)) return [];
    return rawCycles.filter((c: any) => {
      const startDate = c?.start_date ?? c?.startDate;
      const endDate = c?.end_date ?? c?.endDate;
      if (startDate || endDate) {
        const startYear = startDate ? new Date(startDate).getFullYear() : null;
        const endYear = endDate ? new Date(endDate).getFullYear() : null;
        return startYear === year || endYear === year;
      }
      // Keep cycles with no dates (shouldn't happen, but safe fallback)
      return true;
    }).sort(compareCyclesNewestFirst);
  }, [rawCycles, year]);

  // Fetch canonical campaign statuses
  const { data: campaignStatuses = [] } = useQuery<any[]>({
    queryKey: ['/api/campaign-statuses'],
    queryFn: async () => apiJson('/api/campaign-statuses'),
    staleTime: 60_000,
  });

  // Memoized status map
  const campaignStatusMap = useMemo(() => {
    return new Map(campaignStatuses.map((s: any) => [s.id, s]));
  }, [campaignStatuses]);

  // Column handlers
  const handleStatusChange = useCallback((campaignId: number, statusId: number) => {
    setUpdatingCampaignId(campaignId);
    updateCampaignStatusMutation.mutate(
      { campaignId, statusId },
      {
        onSettled: () => {
          setUpdatingCampaignId(null);
        },
      }
    );
  }, [updateCampaignStatusMutation]);

  const handleEditClick = useCallback((campaignId: number) => {
    navigate(`/campaigns/${campaignId}/edit`);
  }, [navigate]);

  const handleDetailedEditClick = useCallback((campaignId: number) => {
    setDetailedEditModalOpen(campaignId);
  }, []);

  const handleViewClick = useCallback((campaignId: number) => {
    saveCampaignsListReturnState(campaignsListState);
    navigate(buildCampaignDetailUrl(campaignId, campaignsListState));
  }, [navigate, campaignsListState]);

  const handleCloneClick = useCallback((campaign: { id: number; name: string; retailerId: number | null }) => {
    setCloneModalState(campaign);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedCampaigns([]);
    setSelectionResetKey((k) => k + 1);
  }, []);

  // Clear selection when filters or page change so users don't act on hidden rows.
  useEffect(() => {
    handleClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerId, cycleId, includeArchived, page, debouncedSearch, year]);

  // Create columns with handlers
  const columns = useMemo(
    () =>
      createCampaignColumns({
        campaignStatuses,
        campaignStatusMap,
        updatingCampaignId,
        onStatusChange: handleStatusChange,
        onEditClick: handleEditClick,
        onDetailedEditClick: handleDetailedEditClick,
        onViewClick: handleViewClick,
        onCloneClick: handleCloneClick,
      }),
    [
      campaignStatuses,
      campaignStatusMap,
      updatingCampaignId,
      handleStatusChange,
      handleEditClick,
      handleDetailedEditClick,
      handleViewClick,
      handleCloneClick,
    ]
  );

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader
          icon={Megaphone}
          title="Campaigns"
          description="Manage your advertising campaigns"
        >
          <YearFilter showIcon={false} onYearChange={handleYearChange} />

          <div className="min-w-[220px]">
            <p className="text-xs text-muted-foreground mb-1">Retailer</p>
            <Select value="all" disabled>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Loading retailers…" />
              </SelectTrigger>
            </Select>
          </div>

          <div className="min-w-[180px]">
            <p className="text-xs text-muted-foreground mb-1">Cycle</p>
            <Select value="all" disabled>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Loading cycles…" />
              </SelectTrigger>
            </Select>
          </div>

          {showIncludeArchived && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                disabled
                className="h-4 w-4"
                aria-label="Include archived"
              />
              <span className="text-sm text-muted-foreground">Include archived</span>
            </div>
          )}
        </PageHeader>

        <TableSkeleton rows={10} className="pt-2" />
        <p className="text-sm text-muted-foreground">Loading campaigns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader
          icon={Megaphone}
          title="Campaigns"
          description="Manage your advertising campaigns"
        />
        <ErrorState
          message={error.message}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={Megaphone}
        title="Campaigns"
        description="Manage your advertising campaigns"
      >
        {/* Year Filter */}
        <YearFilter showIcon={false} onYearChange={handleYearChange} />

        <div className="min-w-[220px]">
          <p className="text-xs text-muted-foreground mb-1">Retailer</p>
          <Select
            value={retailerId === null ? 'all' : String(retailerId)}
            onValueChange={(v) => {
              if (v === 'all') {
                setRetailerId(null);
                setCycleId(null);
              } else {
                setRetailerId(Number(v));
                setCycleId(null);
              }
              setPage(1);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All Retailers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Retailers</SelectItem>
              {retailers.map((r: any) => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[180px]">
          <p className="text-xs text-muted-foreground mb-1">Cycle</p>
          <Select
            value={cycleId === null ? 'all' : String(cycleId)}
            onValueChange={(v) => {
              setCycleId(v === 'all' ? null : Number(v));
              setPage(1);
            }}
            disabled={!enableCycles}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={enableCycles ? "All Cycles" : "Select Retailer First"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cycles</SelectItem>
              {enableCycles && (cyclesError ? (
                <SelectItem value="error" disabled>Unable to load cycles</SelectItem>
              ) : cycles.map((c: any) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              )))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[140px]">
          <p className="text-xs text-muted-foreground mb-1">Status</p>
          <Select
            value={includeArchived ? 'archived' : 'active'}
            onValueChange={(v) => {
              setIncludeArchived(v === 'archived');
              setPage(1);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Only</SelectItem>
              {showIncludeArchived && <SelectItem value="archived">Include Archived</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end pb-0.5">
          <Button
            onClick={() => navigate('/create-campaign')}
            className="flex items-center gap-2 h-9"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </Button>
        </div>
      </PageHeader>

      {showCreateCampaignEmptyState ? (
        <Card>
          <CardHeader>
            <CardTitle>No Campaigns Found</CardTitle>
            <CardDescription>
              You haven't created any campaigns yet. Get started by creating your first campaign.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/create-campaign')}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Your First Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
          <CampaignBulkActionsBar
            selectedCampaigns={selectedCampaigns}
            campaignStatuses={campaignStatuses}
            onClear={handleClearSelection}
          />
          <DataTable
            columns={columns}
            data={campaigns}
            showColumnToggle={true}
            isLoading={isLoading}
            selectionResetKey={selectionResetKey}
            onSelectionChange={setSelectedCampaigns}
            serverSearch={{
              value: search,
              onChange: setSearch,
              placeholder: "Search campaigns...",
              debounceMs: 0,
            }}
            serverPagination={{
              currentPage: page,
              totalPages,
              totalCount,
              pageSize,
              onPageChange: setPage,
            }}
          />
        </div>
      )}

      <Toaster />

      {/* Detailed Edit Modal (Phase 1 Repoint: now using SnapshotEditWorkspace) */}
      {detailedEditModalOpen && (
        <SnapshotEditWorkspace
          campaignId={detailedEditModalOpen}
          isOpen={!!detailedEditModalOpen}
          onClose={() => setDetailedEditModalOpen(null)}
          onSuccess={() => {
            setDetailedEditModalOpen(null);
            window.location.reload();
          }}
        />
      )}

      {/* Clone Campaign Modal */}
      {cloneModalState && (
        <CloneCampaignModal
          campaignId={cloneModalState.id}
          campaignName={cloneModalState.name}
          retailerId={cloneModalState.retailerId}
          isOpen={!!cloneModalState}
          onClose={() => setCloneModalState(null)}
          onSuccess={(newCampaignId) => {
            setCloneModalState(null);
            navigate(buildCampaignDetailUrl(newCampaignId, campaignsListState));
          }}
        />
      )}
    </div>
  );
}
